/**
 * Forge Service
 * 
 * API client for interacting with Forge resources through the Etabli backend.
 * All requests are proxied through the Etabli backend to the Forge API.
 */
import apiClient from './apiClient';
import {
  ForgeClient,
  Forge,
  ForgeWithStats,
  ForgeWithApplications,
  ForgeApplication,
  ForgeApplicationDetails,
  ForgeWithDomain,
  ForgeApplicationWithContext,
  ForgeClientMapping,
  ForgeMapping,
  ForgeApplicationMapping,
  ForgePod,
  ForgeServiceItem,
  ForgeDetail,
  ForgeHealth,
  ForgeArgoCDStatus,
  ForgeLogsParams,
  ForgeLogsResponse,
  BuildLogsResponse,
  CreateClientRequest,
  UpdateClientRequest,
  CreateForgeRequest,
  UpdateForgeRequest,
  CreateStandaloneAppRequest,
  UpdateStandaloneAppRequest,
  AvailableTemplatesResponse,
  CustomAppTemplateInfo,
  CustomAppVariablesResponse,
  CreateServiceBindingRequest,
  ServiceBindingResponse,
} from '../types';

const apiBaseURL = '/forge';

// =============================================================================
// Forge Client endpoints
// =============================================================================

/**
 * List all Forge clients
 */
export const listClients = async (): Promise<ForgeClient[]> => {
  const response = await apiClient.get<ForgeClient[]>(`${apiBaseURL}/clients`);
  return response.data;
};

/**
 * Get a specific Forge client by ID
 */
export const getClient = async (clientId: string): Promise<ForgeClient> => {
  const response = await apiClient.get<ForgeClient>(`${apiBaseURL}/clients/${clientId}`);
  return response.data;
};

// =============================================================================
// Forge endpoints
// =============================================================================

/**
 * List all Forges, optionally filtered by client ID
 */
export const listForges = async (clientId?: string): Promise<Forge[]> => {
  const params = clientId ? { client_id: clientId } : undefined;
  const response = await apiClient.get<Forge[]>(`${apiBaseURL}/forges`, { params });
  return response.data;
};

/**
 * Get a specific Forge by ID
 */
export const getForge = async (forgeId: string): Promise<Forge> => {
  const response = await apiClient.get<Forge>(`${apiBaseURL}/forges/${forgeId}`);
  return response.data;
};

/**
 * Get a Forge with statistics (application counts)
 */
export const getForgeWithStats = async (forgeId: string): Promise<ForgeWithStats> => {
  const response = await apiClient.get<ForgeWithStats>(`${apiBaseURL}/forges/${forgeId}/stats`);
  return response.data;
};

/**
 * Get a Forge with its applications list
 */
export const getForgeWithApplications = async (forgeId: string): Promise<ForgeWithApplications> => {
  const response = await apiClient.get<ForgeWithApplications>(`${apiBaseURL}/forges/${forgeId}/applications`);
  return response.data;
};

/**
 * List Forges with their linked Etabli domain information
 */
export const listForgesWithDomains = async (clientId?: string): Promise<ForgeWithDomain[]> => {
  const params = clientId ? { client_id: clientId } : undefined;
  const response = await apiClient.get<ForgeWithDomain[]>(`${apiBaseURL}/forges-with-domains`, { params });
  return response.data;
};

// =============================================================================
// Application endpoints
// =============================================================================

/**
 * List all Forge applications, optionally filtered by forge ID
 */
export const listApplications = async (forgeId?: string): Promise<ForgeApplication[]> => {
  const params = forgeId ? { forge_id: forgeId } : undefined;
  const response = await apiClient.get<ForgeApplication[]>(`${apiBaseURL}/applications`, { params });
  return response.data;
};

/**
 * Get a specific Forge application by ID
 */
export const getApplication = async (appId: string): Promise<ForgeApplication> => {
  const response = await apiClient.get<ForgeApplication>(`${apiBaseURL}/applications/${appId}`);
  return response.data;
};

/**
 * Get detailed application info including readme and logs
 */
export const getApplicationDetails = async (appId: string): Promise<ForgeApplicationDetails> => {
  const response = await apiClient.get<ForgeApplicationDetails>(`${apiBaseURL}/applications/${appId}/details`);
  return response.data;
};

/**
 * List applications with their linked Etabli use case and cadrage information
 */
export const listApplicationsWithContext = async (forgeId?: string): Promise<ForgeApplicationWithContext[]> => {
  const params = forgeId ? { forge_id: forgeId } : undefined;
  const response = await apiClient.get<ForgeApplicationWithContext[]>(`${apiBaseURL}/applications-with-context`, { params });
  return response.data;
};

// =============================================================================
// Mapping endpoints (CRUD for linking Etabli entities to Forge entities)
// =============================================================================

/**
 * Map an Etabli organization to a Forge client
 */
export const mapOrganizationToClient = async (
  orgId: string,
  forgeClientId: string | null
): Promise<{ message: string; organization_id: string; forge_client_id: string | null }> => {
  const response = await apiClient.put(`${apiBaseURL}/organizations/${orgId}/client`, {
    forge_client_id: forgeClientId,
  } as ForgeClientMapping);
  return response.data;
};

/**
 * Get the Forge client mapping for an organization
 */
export const getOrganizationClientMapping = async (
  orgId: string
): Promise<{ organization_id: string; organization_name: string; forge_client_id: string | null }> => {
  const response = await apiClient.get(`${apiBaseURL}/organizations/${orgId}/client`);
  return response.data;
};

/**
 * Map an Etabli domain to a Forge
 */
export const mapDomainToForge = async (
  domainId: string,
  forgeId: string | null
): Promise<{ message: string; domain_id: string; forge_id: string | null }> => {
  const response = await apiClient.put(`${apiBaseURL}/domains/${domainId}/forge`, {
    forge_id: forgeId,
  } as ForgeMapping);
  return response.data;
};

/**
 * Get the Forge mapping for a domain
 */
export const getDomainForgeMapping = async (
  domainId: string
): Promise<{ domain_id: string; domain_name: string; forge_id: string | null }> => {
  const response = await apiClient.get(`${apiBaseURL}/domains/${domainId}/forge`);
  return response.data;
};

/**
 * Map an Etabli use case to a Forge application
 */
export const mapUseCaseToApplication = async (
  useCaseId: string,
  forgeApplicationId: string | null
): Promise<{ message: string; use_case_id: string; forge_application_id: string | null }> => {
  const response = await apiClient.put(`${apiBaseURL}/use-cases/${useCaseId}/application`, {
    forge_application_id: forgeApplicationId,
  } as ForgeApplicationMapping);
  return response.data;
};

/**
 * Get the Forge application mapping for a use case
 */
export const getUseCaseApplicationMapping = async (
  useCaseId: string
): Promise<{ use_case_id: string; use_case_name: string; forge_application_id: string | null }> => {
  const response = await apiClient.get(`${apiBaseURL}/use-cases/${useCaseId}/application`);
  return response.data;
};

// =============================================================================
// Application Management
// =============================================================================

/**
 * Create a new application in Forge for a use case
 */
export const createApplicationForUseCase = async (
  useCaseId: string,
  appRequest: {
    name: string;
    app_type?: string;
    template_name?: string;
    created_by: string;
    description?: string;
  }
): Promise<{
  message: string;
  use_case_id: string;
  forge_application_id: string;
  application: ForgeApplication;
}> => {
  const response = await apiClient.post(
    `${apiBaseURL}/use-cases/${useCaseId}/application/create`,
    appRequest
  );
  return response.data;
};

/**
 * Trigger a build for an application
 */
export const triggerApplicationBuild = async (
  appId: string,
  buildRequest: {
    branch?: string;
    commit_sha?: string;
    triggered_by: string;
    component_name?: string;
  }
): Promise<{ message: string; build_info: any }> => {
  const response = await apiClient.post(
    `${apiBaseURL}/applications/${appId}/build`,
    buildRequest
  );
  return response.data;
};

/**
 * Delete an application from Forge
 */
export const deleteApplication = async (
  appId: string
): Promise<{ message: string; use_case_id: string }> => {
  const response = await apiClient.delete(`${apiBaseURL}/applications/${appId}`);
  return response.data;
};

// =============================================================================
// Forge Pods and Logs endpoints
// =============================================================================

/**
 * Get list of pods for a specific forge
 */
export const getForgePods = async (
  clientId: string,
  forgeId: string
): Promise<ForgePod[]> => {
  const response = await apiClient.get<ForgePod[]>(
    `${apiBaseURL}/admin/clients/${clientId}/forges/${forgeId}/pods`
  );
  return response.data;
};

/**
 * Get list of pods for a specific custom app inside a forge.
 *
 * Uses the dedicated backend endpoint that applies k8s_safe_name() (46-char
 * truncation) server-side. Replaces the previous client-side filter that
 * broke for apps whose DB name was longer than 46 chars (label mismatch).
 *
 * Etabli backend proxies the backoffice response and returns the unwrapped
 * pods array (matches the shape of getForgePods).
 */
export const getAppPods = async (
  clientId: string,
  forgeId: string,
  appId: string
): Promise<ForgePod[]> => {
  const response = await apiClient.get<ForgePod[]>(
    `${apiBaseURL}/admin/clients/${clientId}/forges/${forgeId}/apps/${appId}/pods`
  );
  return response.data;
};

/**
 * List data services running inside a forge.
 */
export const listForgeServices = async (
  clientId: string,
  forgeId: string
): Promise<ForgeServiceItem[]> => {
  const response = await apiClient.get<ForgeServiceItem[]>(
    `${apiBaseURL}/admin/clients/${clientId}/forges/${forgeId}/services`
  );
  return response.data;
};

/**
 * Reveal the secrets (variable names + values) of a forge data service.
 */
export const revealForgeService = async (
  clientId: string,
  forgeId: string,
  serviceId: string
): Promise<ForgeServiceItem> => {
  const response = await apiClient.post<ForgeServiceItem>(
    `${apiBaseURL}/admin/clients/${clientId}/forges/${forgeId}/services/${serviceId}/reveal`
  );
  return response.data;
};

/**
 * List env variables of a custom app, grouped by applicative service.
 */
export const getCustomAppVariables = async (
  clientId: string,
  forgeId: string,
  appId: string
): Promise<CustomAppVariablesResponse> => {
  const response = await apiClient.get<CustomAppVariablesResponse>(
    `${apiBaseURL}/admin/clients/${clientId}/forges/${forgeId}/apps/${appId}/variables`
  );
  return response.data;
};

/**
 * Bind a forge data service into a custom app (inject env vars in GitOps deployments).
 */
export const createCustomAppServiceBinding = async (
  clientId: string,
  forgeId: string,
  appId: string,
  payload: CreateServiceBindingRequest
): Promise<ServiceBindingResponse> => {
  const response = await apiClient.post<ServiceBindingResponse>(
    `${apiBaseURL}/admin/clients/${clientId}/forges/${forgeId}/apps/${appId}/service-bindings`,
    payload
  );
  return response.data;
};

/**
 * Get the detailed forge view (URLs, infra repo, argocd pods, resource usage).
 */
export const getForgeDetailView = async (
  clientId: string,
  forgeId: string
): Promise<ForgeDetail> => {
  const response = await apiClient.get<ForgeDetail>(
    `${apiBaseURL}/admin/clients/${clientId}/forges/${forgeId}/detail`
  );
  return response.data;
};

/**
 * Get the forge health (status of system & custom apps, URLs, uptime).
 */
export const getForgeHealth = async (
  clientId: string,
  forgeId: string
): Promise<ForgeHealth> => {
  const response = await apiClient.get<ForgeHealth>(
    `${apiBaseURL}/admin/clients/${clientId}/forges/${forgeId}/health`
  );
  return response.data;
};

/**
 * Get the ArgoCD application sync/health status for a forge.
 */
export const getForgeArgoCDStatus = async (
  clientId: string,
  forgeId: string
): Promise<ForgeArgoCDStatus> => {
  const response = await apiClient.get<ForgeArgoCDStatus>(
    `${apiBaseURL}/admin/clients/${clientId}/forges/${forgeId}/argocd-status`
  );
  return response.data;
};

/**
 * Get logs for a specific pod in a forge (returns structured log entries)
 */
export const getForgeLogs = async (
  clientId: string,
  forgeId: string,
  params: ForgeLogsParams
): Promise<ForgeLogsResponse> => {
  const response = await apiClient.get<ForgeLogsResponse>(
    `${apiBaseURL}/admin/clients/${clientId}/forges/${forgeId}/logs`,
    { params }
  );
  return response.data;
};

/**
 * Get logs for a custom app build.
 * GET .../apps/{appId}/build/logs?build_id=...&tail_lines=500
 */
export const getApplicationBuildLogs = async (
  clientId: string,
  forgeId: string,
  appId: string,
  params: { build_id?: string; batch_id?: string; tail_lines?: number; container?: string } = {}
): Promise<BuildLogsResponse> => {
  const response = await apiClient.get<BuildLogsResponse>(
    `${apiBaseURL}/admin/clients/${clientId}/forges/${forgeId}/apps/${appId}/build/logs`,
    { params }
  );
  return response.data;
};

// =============================================================================
// Super-admin management (Client / Forge / Standalone app CRUD)
// Used by the /forge-dashboard tabs. All endpoints are super-admin only.
// =============================================================================

export const createClient = async (payload: CreateClientRequest): Promise<ForgeClient> => {
  const response = await apiClient.post<ForgeClient>(`${apiBaseURL}/clients`, payload);
  return response.data;
};

export const updateClient = async (
  clientId: string,
  payload: UpdateClientRequest
): Promise<ForgeClient> => {
  const response = await apiClient.put<ForgeClient>(
    `${apiBaseURL}/clients/${clientId}`,
    payload
  );
  return response.data;
};

export const deleteClient = async (clientId: string): Promise<void> => {
  await apiClient.delete(`${apiBaseURL}/clients/${clientId}`);
};

export const createForge = async (
  clientId: string,
  payload: CreateForgeRequest
): Promise<Forge> => {
  const response = await apiClient.post<Forge>(
    `${apiBaseURL}/clients/${clientId}/forges`,
    payload
  );
  return response.data;
};

export const updateForge = async (
  clientId: string,
  forgeId: string,
  payload: UpdateForgeRequest
): Promise<Forge> => {
  const response = await apiClient.put<Forge>(
    `${apiBaseURL}/clients/${clientId}/forges/${forgeId}`,
    payload
  );
  return response.data;
};

export const deleteForge = async (clientId: string, forgeId: string): Promise<void> => {
  await apiClient.delete(`${apiBaseURL}/clients/${clientId}/forges/${forgeId}`);
};

export const createStandaloneApplication = async (
  payload: CreateStandaloneAppRequest
): Promise<ForgeApplication> => {
  const { client_id, forge_id } = payload;
  const response = await apiClient.post<ForgeApplication>(
    `${apiBaseURL}/admin/clients/${client_id}/forges/${forge_id}/apps`,
    payload
  );
  return response.data;
};

export const updateStandaloneApplication = async (
  clientId: string,
  forgeId: string,
  appId: string,
  payload: UpdateStandaloneAppRequest
): Promise<ForgeApplication> => {
  const response = await apiClient.put<ForgeApplication>(
    `${apiBaseURL}/admin/clients/${clientId}/forges/${forgeId}/apps/${appId}`,
    payload
  );
  return response.data;
};

export const deleteStandaloneApplication = async (
  clientId: string,
  forgeId: string,
  appId: string
): Promise<void> => {
  await apiClient.delete(
    `${apiBaseURL}/admin/clients/${clientId}/forges/${forgeId}/apps/${appId}`
  );
};

/**
 * List custom-app templates available in Forge (default_app, ecommerce, …).
 * Used by the application creation wizard to let the user choose a template.
 */
export const listAppTemplates = async (): Promise<CustomAppTemplateInfo[]> => {
  const response = await apiClient.get<AvailableTemplatesResponse>(`${apiBaseURL}/templates`);
  return response.data.templates ?? [];
};

// =============================================================================
// Organization forge status
// =============================================================================

/**
 * Check whether the current user's organisation has at least one domain
 * linked to a Forge. DB-only check — no external Forge API call.
 * Super-admins always receive true.
 */
export const checkOrgHasForge = async (): Promise<boolean> => {
  const response = await apiClient.get<{ has_forge: boolean }>(`${apiBaseURL}/has-forge`);
  return response.data.has_forge;
};

// =============================================================================
// Default export as service object
// =============================================================================

const forgeService = {
  // Clients
  listClients,
  getClient,

  // Forges
  listForges,
  getForge,
  getForgeWithStats,
  getForgeWithApplications,
  listForgesWithDomains,

  // Applications
  listApplications,
  getApplication,
  getApplicationDetails,
  listApplicationsWithContext,

  // Mappings
  mapOrganizationToClient,
  getOrganizationClientMapping,
  mapDomainToForge,
  getDomainForgeMapping,
  mapUseCaseToApplication,
  getUseCaseApplicationMapping,

  // Application Management
  createApplicationForUseCase,
  triggerApplicationBuild,
  deleteApplication,

  // Super-admin CRUD (used by /forge-dashboard)
  createClient,
  updateClient,
  deleteClient,
  createForge,
  updateForge,
  deleteForge,
  createStandaloneApplication,
  updateStandaloneApplication,
  deleteStandaloneApplication,
  listAppTemplates,

  // Forge Pods and Logs
  getForgePods,
  getAppPods,
  getForgeLogs,
  getApplicationBuildLogs,

  // Forge data services
  listForgeServices,

  // Custom app variables / service bindings
  getCustomAppVariables,
  createCustomAppServiceBinding,
  revealForgeService,

  // Forge overview / health / ArgoCD status
  getForgeDetailView,
  getForgeHealth,
  getForgeArgoCDStatus,

  // Org forge status
  checkOrgHasForge,
};

export default forgeService;
